import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Chip,
  Paper,
  Grid,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

import { Layout } from "../../components/dashboard/layout";
import axios from "axios";
import keys from "../../constants.js";

import { DataGrid } from "@mui/x-data-grid";

// ---------------------------------------------------------------------------
// Small helpers — kept outside the component so they don't get re-created
// on every render, and so the render code below stays readable.
// ---------------------------------------------------------------------------

// Status -> color mapping used for the colored "pill" chips.
const STATUS_COLORS = {
  Pending: "#EE8523",
  Alloted: "#68ad45",
  Delivered: "#5b7fbd",
};

const StatusChip = ({ status }) => (
  <Chip
    label={status || "—"}
    size="small"
    sx={{
      bgcolor: STATUS_COLORS[status] || "#9e9e9e",
      color: "#fff",
      fontWeight: 600,
      minWidth: 84,
    }}
  />
);

// Grade (e.g. "Grade 3") -> school-level short code (ES / MS / HS).
const gradeInfo = (grade) => {
  if (!grade) return null;
  const gradeNumber = parseInt(String(grade).replace("Grade ", ""), 10);
  if (Number.isNaN(gradeNumber)) return null;
  if (gradeNumber <= 5) return { text: "ES", color: "#68ad45" };
  if (gradeNumber <= 8) return { text: "MS", color: "#EE8523" };
  return { text: "HS", color: "#d71920" };
};

const GradeChip = ({ grade }) => {
  const info = gradeInfo(grade);
  if (!info) return null;
  return (
    <Chip
      label={info.text}
      size="small"
      sx={{ bgcolor: info.color, color: "#fff", fontWeight: 600, minWidth: 44 }}
    />
  );
};

// Turns a single search string into a "does this request match?" test.
// This replaces the four separate filter boxes from the old version —
// one box now searches name, vehicle, decal number and status together.
const matchesSearch = (request, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    request.userName?.toLowerCase().includes(q) ||
    request.vehicleInfo?.toLowerCase().includes(q) ||
    request.decalNo?.toLowerCase().includes(q) ||
    request.status?.toLowerCase().includes(q) ||
    String(request.id).includes(q)
  );
};

const mapRequestRow = (req) => ({
  id: req[6],
  userName: req[0],
  vehicleInfo: req[1],
  status: req[2],
  reqDate: req[3],
  issueDate: req[4],
  decalNo: req[5],
  driverName: req[7],
  driverNumber: req[8],
  grade: req[10],
  students: req[11] ? JSON.parse(req[11]) : [],
  make: req[12],
  model: req[13],
  regNo: req[14],
  color: req[9],
  uniqId: req[15],
});

const DecalRequests = () => {
  const [requests, setRequests] = useState([]); // pending + alloted
  const [deliveredRequests, setDeliveredRequests] = useState([]);
  const [deliveredLoaded, setDeliveredLoaded] = useState(false);
  const [isLoadingDelivered, setIsLoadingDelivered] = useState(false);

  const [tab, setTab] = useState(0); // 0 = Active requests, 1 = Delivered
  const [search, setSearch] = useState("");
  const [deliveredSearch, setDeliveredSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [decalInput, setDecalInput] = useState("");
  const [pickedUpBy, setPickedUpBy] = useState("Self");

  // ---- Data loading ---------------------------------------------------

  const getRequests = () => {
    const url = keys.APIPATH1 + "/getDecalRequest.php";
    axios
      .post(url, {})
      .then((response) => {
        if (response.data && response.data.html) {
          const formatted = response.data.html.map(mapRequestRow);
          setRequests(formatted.filter((r) => r.status !== "Delivered"));
        }
      })
      .catch((error) => {
        console.error("Error fetching requests:", error);
      });
  };

  const fetchDelivered = () => {
    setIsLoadingDelivered(true);
    const url = keys.APIPATH1 + "/getDeliveredDecalRequest.php";
    axios
      .post(url, {})
      .then((response) => {
        if (response.data && response.data.html) {
          setDeliveredRequests(response.data.html.map(mapRequestRow));
        }
        setDeliveredLoaded(true);
      })
      .catch((error) => {
        console.error("Error fetching delivered requests:", error);
      })
      .finally(() => setIsLoadingDelivered(false));
  };

  useEffect(() => {
    getRequests();
  }, []);

  // Load delivered requests the first time the Delivered tab is opened.
  useEffect(() => {
    if (tab === 1 && !deliveredLoaded) {
      fetchDelivered();
    }
  }, [tab, deliveredLoaded]);

  const filteredRequests = useMemo(
    () => requests.filter((r) => matchesSearch(r, search)),
    [requests, search]
  );

  const filteredDeliveredRequests = useMemo(
    () => deliveredRequests.filter((r) => matchesSearch(r, deliveredSearch)),
    [deliveredRequests, deliveredSearch]
  );

  // ---- Dialog handling --------------------------------------------------

  const handleClickOpen = (id, row) => {
    setSelectedRow(row);
    setDecalInput(row.decalNo || "");
    setPickedUpBy(row.pickedUpBy || "Self");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    setDecalInput("");
    setPickedUpBy("Self");
  };

  const handleDecalInputChange = (event) => setDecalInput(event.target.value);
  const handlePickedUpByChange = (event) => setPickedUpBy(event.target.value);

  const handleSubmit = (delivered) => {
    const info = gradeInfo(selectedRow?.grade);
    const data = {
      id: selectedRow?.id,
      vehicleInfo: selectedRow?.vehicleInfo,
      driverName: selectedRow?.driverName,
      driverNumber: selectedRow?.driverNumber,
      displayText: info?.text || "",
      make: selectedRow?.make,
      model: selectedRow?.model,
      regNo: selectedRow?.regNo,
      pickedUpBy,
      students: selectedRow?.students,
      delivered: delivered ? "yes" : "no",
    };

    const url = keys.APIPATH1 + "/approveDecal.php";
    axios
      .post(url, { id: selectedRow.id, decalInput, otherData: data, pickedUpBy })
      .then(() => {
        alert("Done");
        getRequests();
      })
      .catch((error) => {
        console.error("Error approving decal:", error);
      });
    handleClose();
  };

  const handleDeliveredClick = () => {
    const url = keys.APIPATH1 + "/deliverDecal.php";
    axios
      .post(url, { id: selectedRow.id, pickedUpBy })
      .then(() => {
        alert("Marked as Delivered");
        setRequests((prev) => prev.filter((r) => r.id !== selectedRow.id));
        setDeliveredRequests((prev) => [...prev, { ...selectedRow, status: "Delivered" }]);
      })
      .catch((error) => {
        console.error("Error marking as delivered:", error);
      });
    handleClose();
  };

  const handleDecalDelete = (id) => {
    const url = keys.APIPATH1 + "/deleteDecal.php";
    axios
      .post(url, { id })
      .then(() => {
        alert("Done");
        getRequests();
      })
      .catch((error) => {
        console.error("Error deleting decal:", error);
      });
  };

  // ---- Table columns (kept short — everything else lives in the detail

  //      pop-up so the table itself stays quick to scan) ------------------

  const columns = [
    { field: "userName", headerName: "Name", flex: 1.3, minWidth: 160 },
    { field: "vehicleInfo", headerName: "Vehicle", flex: 1, minWidth: 140 },
    {
      field: "grade",
      headerName: "Grade",
      width: 100,
      renderCell: (params) => <GradeChip grade={params.value} />,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    { field: "decalNo", headerName: "Decal No.", width: 130 },
  ];

  const gradeForDialog = gradeInfo(selectedRow?.grade);

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Decal Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review requests, issue a decal number, and mark pick-ups as delivered.
        </Typography>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label={`Active (${requests.length})`} />
            <Tab
              label={
                isLoadingDelivered ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Delivered <CircularProgress size={14} />
                  </Box>
                ) : (
                  `Delivered (${deliveredRequests.length})`
                )
              }
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 0 ? (
              <>
                <TextField
                  fullWidth
                  placeholder="Search by name, vehicle, decal number, or status"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <DataGrid
                  rows={filteredRequests}
                  columns={columns}
                  onRowClick={(params) => handleClickOpen(params.id, params.row)}
                  pageSize={10}
                  autoHeight
                  disableSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "grey.50",
                      fontWeight: 600,
                    },
                  }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  placeholder="Search delivered requests"
                  size="small"
                  value={deliveredSearch}
                  onChange={(e) => setDeliveredSearch(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#fafafa" }}>
                        {["Name", "Vehicle", "Grade", "Decal No.", "Requested", "Issued"].map(
                          (h) => (
                            <th
                              key={h}
                              style={{
                                textAlign: "left",
                                borderBottom: "2px solid #eee",
                                padding: "10px 8px",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#555",
                              }}
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeliveredRequests.map((row) => (
                        <tr
                          key={row.uniqId || row.id}
                          onClick={() => handleClickOpen(row.id, row)}
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            {row.userName}
                          </td>
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            {row.vehicleInfo}
                          </td>
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            <GradeChip grade={row.grade} />
                          </td>
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            {row.decalNo}
                          </td>
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            {row.reqDate}
                          </td>
                          <td style={{ padding: "10px 8px", borderBottom: "1px solid #f0f0f0" }}>
                            {row.issueDate}
                          </td>
                        </tr>
                      ))}
                      {filteredDeliveredRequests.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            style={{ padding: 24, textAlign: "center", color: "#999" }}
                          >
                            No delivered requests match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: "grey.50" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedRow?.userName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <StatusChip status={selectedRow?.status} />
            <GradeChip grade={selectedRow?.grade} />
            {selectedRow?.reqDate && (
              <Chip
                variant="outlined"
                size="small"
                label={`Requested ${selectedRow.reqDate}`}
              />
            )}
            {selectedRow?.issueDate && (
              <Chip variant="outlined" size="small" label={`Issued ${selectedRow.issueDate}`} />
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Vehicle details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Make"
                value={selectedRow?.make || ""}
                onChange={(e) => setSelectedRow({ ...selectedRow, make: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Model"
                value={selectedRow?.model || ""}
                onChange={(e) => setSelectedRow({ ...selectedRow, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Color"
                value={selectedRow?.color || ""}
                onChange={(e) => setSelectedRow({ ...selectedRow, color: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Registration No."
                value={selectedRow?.regNo || ""}
                onChange={(e) => setSelectedRow({ ...selectedRow, regNo: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Driver details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Driver name"
                value={selectedRow?.driverName || ""}
                onChange={(e) => setSelectedRow({ ...selectedRow, driverName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Driver number"
                value={selectedRow?.driverNumber || ""}
                onChange={(e) =>
                  setSelectedRow({ ...selectedRow, driverNumber: e.target.value })
                }
              />
            </Grid>
          </Grid>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: 14 }}>
              Picked up by
            </FormLabel>
            <RadioGroup row value={pickedUpBy} onChange={handlePickedUpByChange}>
              <FormControlLabel value="Self" control={<Radio />} label="Self" />
              <FormControlLabel value="Driver" control={<Radio />} label="Driver" />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Students on this request
          </Typography>
          {selectedRow?.students?.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {selectedRow.students.map((student, index) => (
                <Chip
                  key={index}
                  size="small"
                  variant="outlined"
                  label={`${student.name} — ${student.grade}`}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No students listed.
            </Typography>
          )}

          <TextField
            fullWidth
            label="Decal No."
            value={decalInput}
            onChange={handleDecalInputChange}
            size="small"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
          <Button
            onClick={() => {
              handleDecalDelete(selectedRow?.id);
              handleClose();
            }}
            color="error"
          >
            Delete
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {selectedRow?.status === "Delivered" ? (
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={() => handleSubmit(false)} variant="outlined">
                Save
              </Button>
              <Button onClick={() => handleSubmit(true)} variant="contained">
                Save &amp; deliver
              </Button>
              {selectedRow?.status === "Alloted" && (
                <Button onClick={handleDeliveredClick} variant="contained" color="secondary">
                  Mark as delivered
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default DecalRequests;